import { useCallback } from 'react';

import { InfiniteData } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { PaginatedResponse } from '@/modules/common';
import { GroupBy } from '@/modules/common/types';

import { getAssignmentsByDepartment } from '../../api/assignments-by-department.api';
import {
  buildAssignmentFingerprint,
  buildStateChangeFingerprint,
  buildTimeOffFingerprint,
} from '../../helpers/assignment-cache-utils';
import {
  backfillCrossGroupMemberData,
  deepCopyMember,
  mergeAssignments,
  mergeStateChanges,
  mergeTimeOffs,
  mergeUtilizationByDate,
  PageResponseWithMetadata,
} from '../../helpers/planner-base/planner-pagination-helpers';
import { PlannerDepartmentResponse } from '../../types/dto';
import { useAssignmentsBase } from './use-assignments-base';

interface UseAssignmentsByDepartmentParams {
  filters: FilterValues['values'];
  enabled?: boolean;
}

/**
 * Build a content-level fingerprint for department pages.
 * Must change when mutations patch the cache so select() bypasses its memoization.
 */
function buildDepartmentFingerprint(
  data: InfiniteData<PageResponseWithMetadata<PlannerDepartmentResponse>>
): string {
  return data.pages
    .map(p => {
      const departments = p.items as PlannerDepartmentResponse[];
      const assignmentFingerprint = departments
        .flatMap(d => d.members.flatMap(m => m.assignments.map(buildAssignmentFingerprint)))
        .join(',');
      const timeOffFingerprint = departments
        .flatMap(d => d.members.flatMap(m => m.timeOffs.map(buildTimeOffFingerprint)))
        .join(',');
      const utilizationFingerprint = departments
        .flatMap(d =>
          d.members.map(m => m.utilizationByDate.reduce((sum, u) => sum + u.assigned, 0))
        )
        .join(',');
      // Group-level utilization MUST be in the fingerprint too. ignorePlaceholdersForGroup
      // changes only the department's aggregate utilizationByDate (placeholders excluded),
      // not member-level data — so without this the select() memo stays stale and the group
      // row doesn't update until a full reload.
      const groupUtilizationFingerprint = departments
        .map(d => d.utilizationByDate.reduce((sum, u) => sum + u.assigned + u.capacity, 0))
        .join(',');
      // Member-info column fields (location, department, capacity, rates, …) live on the
      // state changes, not on assignments — include them so editing them re-runs select().
      const stateChangeFingerprint = departments
        .flatMap(d =>
          d.members.flatMap(m => m.stateChanges?.map(buildStateChangeFingerprint) ?? [])
        )
        .join(',');
      return `${p.items.length}:${p.pagination?.cursor}:${p._dateRange.startDate}:${assignmentFingerprint}:${timeOffFingerprint}:${utilizationFingerprint}:${groupUtilizationFingerprint}:${stateChangeFingerprint}`;
    })
    .join('|');
}

/**
 * Merges department data across all loaded pages (vertical + horizontal pagination).
 *
 * TODO: Currently merges ALL users from ALL loaded date ranges within each department.
 * Users who only have data in a specific date range still appear when scrolling
 * to other periods. Ideally, visibility should be filtered by the current viewport.
 * Related: Same issue exists in use-assignments-by-member.ts and use-assignments-by-project.ts
 */
function selectDepartmentPages(
  data: InfiniteData<PageResponseWithMetadata<PlannerDepartmentResponse>>
): PaginatedResponse<PlannerDepartmentResponse> {
  // Combine all loaded pages to show the full visible date range
  const allItems = data.pages.flatMap(page => page.items);

  // Merge assignments for the same department across different date ranges
  const mergedDepartmentsMap = new Map<number | 'null', PlannerDepartmentResponse>();
  const departmentMembersMap = new Map<
    number | 'null',
    Map<number, PlannerDepartmentResponse['members'][number]>
  >();

  allItems.forEach(plannerDepartmentResponse => {
    // Use 'null' string as key for departments without a department (null department)
    const departmentKey = plannerDepartmentResponse.department?.id ?? ('null' as const);

    const existingDepartment = mergedDepartmentsMap.get(departmentKey);

    if (existingDepartment) {
      const membersById = departmentMembersMap.get(departmentKey);
      if (!membersById) return;

      // Merge members from different date ranges for the same department
      plannerDepartmentResponse.members.forEach(newMember => {
        const existingMember = membersById.get(newMember.user.id);

        if (existingMember) {
          // Merge assignments and their activePeriods across date ranges
          mergeAssignments(existingMember.assignments, newMember.assignments);

          // Merge time-off requests across date ranges
          mergeTimeOffs(existingMember.timeOffs, newMember.timeOffs);

          // Merge state changes across date ranges
          existingMember.stateChanges = mergeStateChanges(
            existingMember.stateChanges,
            newMember.stateChanges
          );

          // Merge utilization by date, avoiding duplicates
          existingMember.utilizationByDate = mergeUtilizationByDate(
            existingMember.utilizationByDate,
            newMember.utilizationByDate
          );
        } else {
          // Add new member to the department — deep copy mutable arrays
          const newMemberCopy = deepCopyMember(newMember);
          existingDepartment.members.push(newMemberCopy);
          membersById.set(newMemberCopy.user.id, newMemberCopy);
        }
      });

      // Merge department-level utilization by date, avoiding duplicates
      existingDepartment.utilizationByDate = mergeUtilizationByDate(
        existingDepartment.utilizationByDate,
        plannerDepartmentResponse.utilizationByDate
      );
    } else {
      // Deep copy members and their mutable arrays to prevent select() from
      // mutating TanStack Query's cached data across re-runs.
      // Deduplicate members by user.id first — the backend may return the same
      // member multiple times within a single department item.
      const membersById = new Map<number, PlannerDepartmentResponse['members'][number]>();
      plannerDepartmentResponse.members.forEach(member => {
        const existing = membersById.get(member.user.id);
        if (existing) {
          mergeAssignments(existing.assignments, member.assignments);
          mergeTimeOffs(existing.timeOffs, member.timeOffs);
          existing.stateChanges = mergeStateChanges(existing.stateChanges, member.stateChanges);
          existing.utilizationByDate = mergeUtilizationByDate(
            existing.utilizationByDate,
            member.utilizationByDate
          );
        } else {
          membersById.set(member.user.id, deepCopyMember(member));
        }
      });

      const departmentCopy = {
        ...plannerDepartmentResponse,
        utilizationByDate: [...plannerDepartmentResponse.utilizationByDate],
        members: Array.from(membersById.values()),
      };

      mergedDepartmentsMap.set(departmentKey, departmentCopy);
      departmentMembersMap.set(departmentKey, membersById);
    }
  });

  // Backfill missing data for users that appear in multiple departments
  // (e.g., after horizontal pagination when user changed departments)
  backfillCrossGroupMemberData(departmentMembersMap);

  // Get total from first page (represents total departments, not affected by date range)
  const total = data.pages[0]?.pagination?.total ?? 0;

  // Don't sort — preserve insertion order from the backend so that new
  // vertical pages simply append rows at the bottom without reordering
  // existing rows (which causes visible UI jumps).
  return new PaginatedResponse<PlannerDepartmentResponse>({
    items: Array.from(mergedDepartmentsMap.values()),
    pagination: {
      total,
      cursor: data.pages[data.pages.length - 1]?.pagination?.cursor ?? null,
    },
  });
}

/**
 * Hook for fetching planner assignments grouped by department.
 * Supports dual-axis infinite scroll:
 * - Horizontal: Load past/future date ranges
 * - Vertical: Load more departments via cursor pagination
 */
export function useAssignmentsByDepartment({
  filters,
  enabled = true,
}: UseAssignmentsByDepartmentParams) {
  // For department grouping, the backend's pageSize is member-weighted:
  // a department with 5 members counts as 5 towards the page size limit.
  // So we weight each item by its member count for dynamic page size calculation.
  const getItemWeight = useCallback(
    (item: PlannerDepartmentResponse) => item.members?.length ?? 1,
    []
  );

  return useAssignmentsBase<PlannerDepartmentResponse>(
    { filters, enabled },
    {
      groupByKey: GroupBy.DEPARTMENTS,
      queryKeyPrefix: 'assignments-by-department',
      logTag: 'useAssignmentsByDepartment',
      apiFn: getAssignmentsByDepartment,
      selectFn: selectDepartmentPages,
      buildFingerprint: buildDepartmentFingerprint,
      getItemWeight,
    }
  );
}
